import { z } from 'zod';

import type { PatientMetadata, PatientOut } from '../api/patients-api';

export const patientFormSchema = z.object({
  custom_fields: z.record(z.string(), z.string()).optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  allergies: z.string().optional(),
  medications: z.string().optional(),
  family_history: z.string().optional(),
  social_history: z.string().optional(),

  medical_history: z.string().optional(),
  surgical_history: z.string().optional(),
  obstetric_history: z.string().optional(),
  insurance_provider: z.string().optional(),
  insurance_group_number: z.string().optional(),
  emergency_contact_name: z.string().optional(),
  insurance_policy_number: z.string().optional(),
  emergency_contact_phone: z.string().optional(),
  last_name: z.string().min(1, 'Last name is required'),
  emergency_contact_relationship: z.string().optional(),
  first_name: z.string().min(1, 'First name is required'),
  medical_record_number: z.string().min(1, 'MRN is required'),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
  gender: z.enum(['M', 'F', 'O'], { message: 'Gender is required' }),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
});

export type PatientFormValues = z.infer<typeof patientFormSchema>;

export const EMPTY_PATIENT_FORM: PatientFormValues = {
  phone: '',
  email: '',
  address: '',
  last_name: '',
  allergies: '',
  first_name: '',
  date_of_birth: '',
  insurance_provider: '',
  medical_record_number: '',
  insurance_group_number: '',
  emergency_contact_name: '',
  insurance_policy_number: '',
  emergency_contact_phone: '',
  emergency_contact_relationship: '',
  gender: undefined as unknown as PatientFormValues['gender'], // required; unset until chosen
};

/**
 * Build the FULL metadata object from form values. Spreads `rawMetadata`
 * first so backend's whole-blob replace never drops unmodeled/extra keys.
 * MRN is a top-level patient field, not metadata, so it is excluded here.
 */
export function formValuesToMetadata(values: PatientFormValues, rawMetadata?: PatientMetadata | null): PatientMetadata {
  const allergies = values.allergies
    ? values.allergies
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
    : [];

  const insurance = values.insurance_provider
    ? {
        provider: values.insurance_provider,
        policy_number: values.insurance_policy_number || '',
        group_number: values.insurance_group_number || undefined,
      }
    : undefined;

  const emergency_contact = values.emergency_contact_name
    ? {
        name: values.emergency_contact_name,
        phone: values.emergency_contact_phone || '',
        relationship: values.emergency_contact_relationship || '',
      }
    : undefined;

  return {
    ...rawMetadata,
    custom_fields: values.custom_fields ?? rawMetadata?.custom_fields,
    allergies,
    insurance,
    emergency_contact,
    gender: values.gender,
    last_name: values.last_name,
    first_name: values.first_name,

    phone: values.phone || undefined,
    email: values.email || undefined,
    date_of_birth: values.date_of_birth,
    address: values.address || undefined,
    medications: values.medications || undefined,
    family_history: values.family_history || undefined,
    social_history: values.social_history || undefined,
    medical_history: values.medical_history || undefined,
    surgical_history: values.surgical_history || undefined,
    obstetric_history: values.obstetric_history || undefined,
  };
}

/** Flatten a patient (metadata + top-level MRN) into form values. */
export function patientToFormValues(patient: PatientOut): PatientFormValues {
  const m = patient.metadata_ ?? {};
  return {
    custom_fields: m.custom_fields ?? {},
    phone: m.phone ?? '',
    email: m.email ?? '',
    address: m.address ?? '',
    last_name: m.last_name ?? '',
    first_name: m.first_name ?? '',
    medications: m.medications ?? '',
    date_of_birth: m.date_of_birth ?? '',
    family_history: m.family_history ?? '',
    social_history: m.social_history ?? '',
    medical_history: m.medical_history ?? '',
    allergies: (m.allergies ?? []).join(', '),
    surgical_history: m.surgical_history ?? '',
    obstetric_history: m.obstetric_history ?? '',
    insurance_provider: m.insurance?.provider ?? '',
    medical_record_number: patient.medical_record_number,
    insurance_group_number: m.insurance?.group_number ?? '',
    emergency_contact_name: m.emergency_contact?.name ?? '',
    insurance_policy_number: m.insurance?.policy_number ?? '',
    emergency_contact_phone: m.emergency_contact?.phone ?? '',
    emergency_contact_relationship: m.emergency_contact?.relationship ?? '',
    gender: m.gender === 'M' || m.gender === 'F' || m.gender === 'O' ? m.gender : EMPTY_PATIENT_FORM.gender,
  };
}
